import { useState, KeyboardEvent } from "react";
import { LangData } from "../../../lib/newsUtils";
import RichTextEditor from "./RichTextEditor";

export type { LangData };

interface LangFieldsProps {
  langCode: string;
  data: LangData;
  onChange: (updated: LangData) => void;
  isDefault: boolean;
}

export default function LangFields({ langCode, data, onChange, isDefault }: LangFieldsProps) {
  const [tagInput, setTagInput] = useState("");

  const set = (field: keyof LangData, value: string | string[]) =>
    onChange({ ...data, [field]: value });

  const addTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !data.tags.includes(trimmed)) {
      set("tags", [...data.tags, trimmed]);
      setTagInput("");
    }
  };

  const removeTag = (tag: string) =>
    set("tags", data.tags.filter((t) => t !== tag));

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
  };

  const labelClass =
    "block text-xs font-semibold text-navy/60 uppercase tracking-wider mb-1.5";
  const inputClass =
    "w-full px-3.5 py-2.5 text-sm text-navy bg-cool-gray-50 border border-cool-gray-200 rounded-md focus:outline-none focus:border-navy transition-colors";

  return (
    <div className="space-y-5">
      {/* Title */}
      <div>
        <label className={labelClass}>
          Title {isDefault && <span className="text-red-400">*</span>}
        </label>
        <input
          type="text"
          value={data.title}
          onChange={(e) => set("title", e.target.value)}
          required={isDefault}
          placeholder={
            isDefault
              ? "Article title"
              : "Article title in this language (optional)"
          }
          className={inputClass}
        />
      </div>

      {/* Category Label */}
      <div>
        <label className={labelClass}>
          Category Label {isDefault && <span className="text-red-400">*</span>}
        </label>
        <input
          type="text"
          value={data.category_label}
          onChange={(e) => set("category_label", e.target.value)}
          required={isDefault}
          placeholder="e.g. Open Call"
          className={inputClass}
        />
      </div>

      {/* Excerpt */}
      <div>
        <label className={labelClass}>
          Excerpt / Short Description{" "}
          {isDefault && <span className="text-red-400">*</span>}
        </label>
        <textarea
          value={data.excerpt}
          onChange={(e) => set("excerpt", e.target.value)}
          required={isDefault}
          rows={3}
          placeholder="Short summary shown in the news list"
          className={`${inputClass} resize-vertical`}
          maxLength={500}
        />
        <p className="text-right text-xs text-navy/30 mt-1">
          {data.excerpt.length}/500
        </p>
      </div>

      {/* Body */}
      <div>
        <label className={labelClass}>
          Full Article Body{" "}
          {isDefault && <span className="text-red-400">*</span>}
        </label>
        <RichTextEditor
          key={`body-${langCode}`}
          value={data.body}
          onChange={(html) => set("body", html)}
          placeholder="Write the full article here…"
          minHeight="260px"
        />
        {isDefault && (
          <input
            type="text"
            required
            value={data.body.replace(/<[^>]*>/g, "").trim()}
            onChange={() => {}}
            tabIndex={-1}
            aria-hidden="true"
            className="sr-only"
            title="Article body is required"
          />
        )}
      </div>

      {/* Tags */}
      <div>
        <label className={labelClass}>Tags</label>
        <div className="flex gap-2 mb-2 flex-wrap">
          {data.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-navy/8 text-navy text-xs font-medium rounded-full"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="w-3.5 h-3.5 flex items-center justify-center text-navy/40 hover:text-red-500 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xs" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            placeholder="Type tag and press Enter"
            className="flex-1 px-3.5 py-2.5 text-sm text-navy bg-cool-gray-50 border border-cool-gray-200 rounded-md focus:outline-none focus:border-navy transition-colors"
          />
          <button
            type="button"
            onClick={addTag}
            className="px-4 py-2 text-xs font-semibold text-navy border border-cool-gray-200 rounded-md hover:bg-cool-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Add Tag
          </button>
        </div>
        <p className="text-xs text-navy/35 mt-1">
          Press Enter or comma to add a tag
        </p>
      </div>
    </div>
  );
}
